import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  AssignVehiclesRequestBody,
  AuthSupportPagedList, RemoveVehiclesRequestBody,
  SubscribeUsersRequestBody, SubscriptionUser,
  SubscriptionUsersPagedList,
  TransactionPagedList,
  UnsubscribeUsersRequestBody,
  VehiclePagedList
} from 'src/swagger-gen/web-secured/models';
import {
  Subscription,
  SubscriptionCreate,
  SubscriptionPagedList,
} from 'src/swagger-gen/web-secured/models';
import { BaseService } from '../services/base.service';
import { BadgeRequest } from '../shared/models/badge/badge-request';
import { SubscriptionRepoModelMapper } from './subscriptions/mapper/subscriptions-mapper';
import { SubscriptionUserPageable } from '../models/subscription-user/subscription-user-pageable';
import { SubscriptionVehiclePageable } from '../models/subscription-vehicle/subscription-vehicle-pageable';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionRepo extends BaseService {
  subscriptionUrl = environment.apiUrl + '/subscriptions';
  customSubscriptionUrl = environment.apiUrl + '/custom/subscriptions';
  readonly BADGES_LIMIT = '20';

  constructor(private http: HttpClient, private logger: NGXLogger) {
    super();
  }

  /**
   * Get all subscriptions
   */
  getSubscriptionList(
    offset: number,
    limit: number,
    params: any[],
    search: string
  ): Observable<SubscriptionPagedList> {
    const url = this.subscriptionUrl + '/search';
    let requestString = this.pagination(offset, limit, this.BADGES_LIMIT);
    let queryParams = '';
    params.forEach((p: any) => {
      queryParams += '&' + p.key + '=' + p.value;
    });
    queryParams += '&search=' + search;
    requestString += queryParams;
    const requestParams = new HttpParams({ fromString: requestString });
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      params: requestParams,
    };

    return this.http.post<SubscriptionPagedList>(url, null, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`Get subscription list from backend`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  /**
   * Get subscription by uid
   * @param uid subscription uid
   */
  getSubscription(uid: string): Observable<Subscription> {
    const url = this.subscriptionUrl + `/${uid}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.get<Subscription>(url, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`Get a subscription from backend`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  assignVehicleToSubscription(uid: string, body: AssignVehiclesRequestBody): Observable<any> {
    const url = `${this.subscriptionUrl}/${uid}/vehicles/assign`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.post<any>(url, body, httpOptions).pipe(
        tap((response) => {
          this.logger.log(`Assign vehicle response from backend`, response);
        }), // DEBUG
        catchError(this.handleServerError)
    );
  }

  removeVehicleToSubscription(uid: string, body: RemoveVehiclesRequestBody): Observable<any> {
    const url = `${this.customSubscriptionUrl}/${uid}/vehicles/remove`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.post<any>(url, body, httpOptions).pipe(
        tap((response) => {
          this.logger.log(`Remove vehicle response from backend`, response);
        }), // DEBUG
        catchError(this.handleServerError)
    );
  }

  /**
   * Assign badge to subscription
   * @param uid subscription uid
   * @param body badge info
   */
  assignBadgeToSubscription(uid: string, body: BadgeRequest): Observable<any> {
    const url = `${this.subscriptionUrl}/${uid}/badges/assign`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.post<any>(url, body, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`Assign badge response from backend`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  /**
   * Unassign badge from subscription
   * @param uid subscription uid
   * @param body badge info
   */
  unassignBadgeFromSubscription(
    uid: string,
    body: any
  ): Observable<any> {
    const url = `${this.subscriptionUrl}/${uid}/badges/remove`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.post<any>(url, body, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`Unassign badge response from backend`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  /**
   * Get badges from subscription
   * @param uid subscription uid
   */
  getBadgesFromSubscription(uid: string): Observable<BadgeRequest[]> {
    const url = `${this.subscriptionUrl}/${uid}/badges`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.get<BadgeRequest[]>(url, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`Badge list response from backend`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  exportUserDefinitionOfSubscribe(uid: string): Observable<any> {
    const url = `${this.subscriptionUrl}/${uid}/users/export`;

    const httpOptions: any = {
      headers: this.httpHeaders(),
      responseType: 'blob' as 'json',
      observe: 'response',
    };

    return this.http
      .get<any>(url, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  exportVehicleDefinitionOfSubscribe(uid: string): Observable<any> {
    const url = `${this.subscriptionUrl}/${uid}/vehicles/export`;

    const httpOptions: any = {
      headers: this.httpHeaders(),
      responseType: 'blob' as 'json',
      observe: 'response',
    };

    return this.http
      .get<any>(url, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  importUserDefinitionOfSubscrition(file: any, uid: string): Observable<any> {
    const url = `${this.subscriptionUrl}/${uid}/users/import`;

    const httpOptions: any = {
      headers: new HttpHeaders().set(
        'Content-Type',
        'application/vnd.ms-excel'
      ),
      observe: 'response',
    };

    return this.http
      .post<any>(url, file, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  importVehicleDefinitionOfSubscrition(
    file: any,
    uid: string
  ): Observable<any> {
    const url = `${this.subscriptionUrl}/${uid}/vehicles/import`;

    const httpOptions: any = {
      headers: new HttpHeaders().set(
        'Content-Type',
        'application/vnd.ms-excel'
      ),
      observe: 'response',
    };

    return this.http
      .post<any>(url, file, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  createSubscription(body: SubscriptionCreate): Observable<any> {
    const url = this.subscriptionUrl + '';

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      observe: 'response' as 'body',
    };

    return this.http
      .post<any>(url, body, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  updateSubscription(uid: string, body: SubscriptionCreate): Observable<any> {
    const url = this.subscriptionUrl + '/' + uid;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .put<any>(url, body, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  deleteSubscription(uid: string): Observable<any> {
    const url = this.subscriptionUrl + '/' + uid;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .delete<any>(url, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  getTagsFromSubscription(
    uid: string,
    offset: number,
    limit: number,
    params: any[],
    body: any
  ): Observable<AuthSupportPagedList> {
    const url = `${this.subscriptionUrl}/${uid}/badges/search`;
    let requestString = this.pagination(offset, limit, this.BADGES_LIMIT);
    let queryParams = '';
    params.forEach((p: any) => {
      queryParams += '&' + p.key + '=' + p.value;
    });
    requestString += queryParams;
    const requestParams = new HttpParams({ fromString: requestString });
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      params: requestParams,
    };

    return this.http.post<AuthSupportPagedList>(url, body, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`Get tags list from backend`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  getTransactionsFromSubscription(
    uid: string,
    offset: number,
    limit: number,
    params: any[],
    body: any
  ): Observable<TransactionPagedList> {
    const url = `${this.subscriptionUrl}/${uid}/transactions/search`;
    let requestString = this.pagination(offset, limit, this.BADGES_LIMIT);
    let queryParams = '';
    params.forEach((p: any) => {
      queryParams += '&' + p.key + '=' + p.value;
    });
    requestString += queryParams;
    const requestParams = new HttpParams({ fromString: requestString });
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      params: requestParams,
    };

    return this.http.post<TransactionPagedList>(url, body, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`Get transaction list from backend`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  exportTagsFromSubscription(uid: string): Observable<any> {
    const url = `${this.subscriptionUrl}/${uid}/badges/export`;

    const httpOptions: any = {
      headers: this.httpHeaders(),
      responseType: 'blob' as 'json',
      observe: 'response',
    };

    return this.http
      .get<any>(url, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  exportTransactionsFromSubscription(uid: string): Observable<any> {
    const url = `${this.subscriptionUrl}/${uid}/transactions/export`;

    const httpOptions: any = {
      headers: this.httpHeaders(),
      responseType: 'blob' as 'json',
      observe: 'response',
    };

    return this.http
      .get<any>(url, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  subscribeUsersToSubscription(subPlanUid: string, body: SubscribeUsersRequestBody): Observable<any> {
    const url = `${this.subscriptionUrl}/${subPlanUid}/users/subscribe`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .post<any>(url, body, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  unsubscribeUsersFromSubscription(subPlanUid: string, body: UnsubscribeUsersRequestBody): Observable<any> {
    const url = `${this.subscriptionUrl}/${subPlanUid}/users/unsubscribe`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .post<any>(url, body, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  getUsersOfSubscription(offset: number, limit: number, subPlanUid: string): Observable<SubscriptionUserPageable> {
    const url = `${this.subscriptionUrl}/${subPlanUid}/users`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      params: new HttpParams()
        .set('offset', offset)
        .set('limit', limit)
    };

    return this.http
      .get<SubscriptionUsersPagedList>(url, httpOptions)
      .pipe(
        map(SubscriptionRepoModelMapper.toSubscriptionUserPaged),
        catchError(this.handleServerError)
        );
  }

  getUserOfSubscription(subPlanUid: string, userUid: string): Observable<SubscriptionUser> {
    const url = `${this.subscriptionUrl}/${subPlanUid}/users/${userUid}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.get<SubscriptionUser>(url, httpOptions).pipe(
        tap((response) => {
          this.logger.log(`Get a user subscribed from backend`, response);
        }), // DEBUG
        catchError(this.handleServerError)
    );
  }

  getVehiclesOfSubscription(offset: number, limit: number, subPlanUid: string): Observable<SubscriptionVehiclePageable> {
    const url = `${this.subscriptionUrl}/${subPlanUid}/vehicles`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      params: new HttpParams()
        .set('offset', offset)
        .set('limit', limit)
    };

    return this.http
      .get<VehiclePagedList>(url, httpOptions)
      .pipe(
        map(SubscriptionRepoModelMapper.toSubscriptionVehiclePaged),
        catchError(this.handleServerError)
        );
  }
}
